import React, { FC, useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, message, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import RegistrationWidget from 'src/components/dashboard/common-widget/registration-widget/registration-widget';
import TitleBox from 'src/components/title-box/title-box';
import { i18nKey } from 'src/locales/i18n';
import OnBoardingRegisterPage from '../../onboarding/onboarding';
import styles from './company-detail.module.less';
import QuitConfirmPopup from 'src/components/registration/quit-confirmer/quit-confirm-popup';
import { useNavigate } from 'react-router-dom';
import { PAGE_ROUTE } from 'src/constants/route';
import { COMPANY_ONBOARDING_TYPE, ONBOARDING_CURRENCY_TYPE, CURRENCY_OPTIONS } from 'src/constants';
import { ICompanyInfo } from 'src/interfaces/accounts/company';
import { ICorporateOnboardingStore } from 'src/store/corporate/onboarding.store';
import useStore from 'src/hooks/useStore';
import { IUserStore } from 'src/store/user';
import { IApplicationStore } from 'src/store/root/application.store';
import CurrencyInput from 'src/components/input-with-currency/input-with-currency';
import moment from 'moment';
import {
	// normalizeInputBlockCharacter,
	normalizeInputBlockNumber
	// normalizeInputBlockSpecialCharacter
} from 'src/helpers/common.utils';
import { scrollPageToTop } from 'src/helpers/window';
// import { CHARACTER_REGEX, NUMBER_REGEX } from 'src/constants/validation';
import ContactPerson from './contact-person/contact-person';

const CompanyDetailPage: FC = () => {
	const [saving, setSaving] = useState(false);
	const [quitModalOpen, setQuitModalOpen] = useState(false);

	const [isMandatoryCiNo, setMandatoryCiNo] = useState(true);
	const [isMandatoryBrRegistrationNo, setMandatoryBrRegistrationNo] = useState(true);
	// const [isOpenOtherSourceOfFunds, setiIOpenOtherSourceOfFunds] = useState<boolean>(false);

	const [t] = useTranslation();

	const corporateOnboardingStore: ICorporateOnboardingStore = useStore('corporateOnboardingStore');
	const userStore: IUserStore = useStore('userStore');
	const applicationStore: IApplicationStore = useStore('applicationStore');
	const [currency, setCurrency] = useState(
		corporateOnboardingStore.companyDetail && corporateOnboardingStore.companyDetail?.referenceCurrency
			? corporateOnboardingStore.companyDetail?.referenceCurrency
			: ONBOARDING_CURRENCY_TYPE.HKD
	);

	const navigator = useNavigate();

	const [form] = Form.useForm();

	const onSaveClick = async () => {
		setSaving(true);
		try {
			await form.validateFields();
			const values: ICompanyInfo = form.getFieldsValue();
			const rs = await corporateOnboardingStore.saveCompanyDetail(values);
			setSaving(false);
			if (rs) {
				message.success(t(i18nKey.feedback.saveAccountSuccess));
				if (corporateOnboardingStore.companyOnboardingType === COMPANY_ONBOARDING_TYPE.FUNDER) {
					navigator(PAGE_ROUTE.FUNDER_COMPANY_REQUEST_FORM);
				} else {
					navigator(PAGE_ROUTE.BORROWER_COMPANY_REQUEST);
				}
			}
		} catch (e) {
			console.log(e);
			const firstInvalidFormItem = form.getFieldsError()[0];
			if (firstInvalidFormItem) {
				form.scrollToField(firstInvalidFormItem.name[0], {
					behavior: 'smooth'
				});
			}
			setSaving(false);
		}
	};

	const onCancelClick = () => {
		setQuitModalOpen(true);
	};

	const onModalClose = () => {
		setQuitModalOpen(false);
	};

	const onModalQuit = () => {
		setQuitModalOpen(false);
		navigator(PAGE_ROUTE.DASHBOARD);
	};

	const changeCiOrBrNo = (field: 'ciNo' | 'brRegistrationNo') => {
		const formValues = form.getFieldValue(field);
		const ci = form.getFieldValue(['ciNo']);
		const br = form.getFieldValue(['brRegistrationNo']);
		if (field == 'ciNo') {
			if (formValues) {
				if (br) {
					setMandatoryBrRegistrationNo(true);
				} else {
					setMandatoryBrRegistrationNo(false);
					setMandatoryCiNo(true);
					form.resetFields(['brRegistrationNo']);
				}
			} else {
				if (br) {
					setMandatoryBrRegistrationNo(true);
					setMandatoryCiNo(false);
					form.resetFields([field]);
				} else {
					setMandatoryBrRegistrationNo(true);
					setMandatoryCiNo(true);
				}
			}
		}
		if (field == 'brRegistrationNo') {
			if (formValues) {
				if (ci) {
					setMandatoryCiNo(true);
				} else {
					setMandatoryBrRegistrationNo(true);
					setMandatoryCiNo(false);
					form.resetFields(['ciNo']);
				}
			} else {
				if (ci) {
					setMandatoryCiNo(true);
					form.resetFields([field]);
					setMandatoryBrRegistrationNo(false);
				} else {
					setMandatoryBrRegistrationNo(true);
					setMandatoryCiNo(true);
				}
			}
		}
	};

	useEffect(() => {
		setSaving(false);
		corporateOnboardingStore.loadProgress(COMPANY_ONBOARDING_TYPE.FUNDER).then(() => {
			setSaving(false);
		});
		scrollPageToTop();
	}, []);

	useEffect(() => {
		if (!form.isFieldsTouched()) {
			form.setFieldsValue({
				...corporateOnboardingStore.companyDetail,
				name:
					corporateOnboardingStore.companyDetail && corporateOnboardingStore.companyDetail.name
						? corporateOnboardingStore.companyDetail.name
						: userStore.companyName,
				referenceCurrency:
					corporateOnboardingStore.companyDetail && corporateOnboardingStore.companyDetail.referenceCurrency
						? corporateOnboardingStore.companyDetail.referenceCurrency
						: ONBOARDING_CURRENCY_TYPE.HKD
			});
		}
	}, [corporateOnboardingStore.companyDetail]);

	return (
		<OnBoardingRegisterPage>
			<RegistrationWidget title={`${t(i18nKey.onboarding.funder.accountTitle)}`}>
				<Form
					layout="vertical"
					form={form}
					initialValues={{
						...corporateOnboardingStore.companyDetail,
						name: corporateOnboardingStore.companyDetail
							? corporateOnboardingStore.companyDetail.name
							: userStore.companyName,
						referenceCurrency: corporateOnboardingStore.companyDetail?.referenceCurrency || ONBOARDING_CURRENCY_TYPE.HKD
					}}>
					<Row gutter={[32, 16]} className="common-padding">
						<Col xs={24} sm={24} md={24} lg={12}>
							<Form.Item label={`${t(i18nKey.onboarding.request.employment.currency)}`} style={{ marginBottom: 0 }}>
								<Row>
									<Col xs={24} sm={24} md={24} lg={6} style={{ width: '100%' }}>
										<Form.Item name="referenceCurrency" noStyle>
											<Select
												options={CURRENCY_OPTIONS}
												style={{ width: '100%' }}
												onChange={(e) => {
													setCurrency(e);
												}}
												placeholder={`${t(i18nKey.common.select)}`}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</Col>
					</Row>
					<TitleBox>
						<span>{t(i18nKey.onboarding.corporate.companyDetail.title)}</span>
					</TitleBox>
					<div className={styles.subLevelForm}>
						<Row gutter={[32, 16]}>
							<Col span={24}>
								<Form.Item
									name="name"
									label={t(i18nKey.registration.companyName)}
									required={false}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input disabled value={''} />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col span={24}>
								<Form.Item
									name="registeredAddress"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.companyAddress)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="ciNo"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.ciNo)}`}
									required={isMandatoryCiNo}
									rules={[
										{
											required: isMandatoryCiNo,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input
										onChange={() => {
											changeCiOrBrNo('ciNo');
										}}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="brRegistrationNo"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.brRegNo)}`}
									required={isMandatoryBrRegistrationNo}
									rules={[
										{
											required: isMandatoryBrRegistrationNo,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input
										onChange={() => {
											changeCiOrBrNo('brRegistrationNo');
										}}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="dateOfIncorporation"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.dateOfInCorporate)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<DatePicker
										placeholder="Select Date"
										className={styles.date}
										disabledDate={(date) => {
											return date.isAfter(moment());
										}}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="placeOfIncorporation"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.placeOfInCorporate)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Select
										placeholder={`${t(i18nKey.common.select)}`}
										options={applicationStore.listOfCountry}
										showSearch
										filterOption={(input, option) =>
											new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
										}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="natureOfBusiness"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.natureOfBiz)}`}
									required={true}
									normalize={normalizeInputBlockNumber}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name="shareholdingStructure"
									label={`${t(i18nKey.onboarding.corporate.companyDetail.shareholdingStucture)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[32, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label={`${t(i18nKey.onboarding.corporate.companyDetail.annualSales)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<CurrencyInput
										name="annualSales"
										currency={currency}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item required={true} label={`${t(i18nKey.onboarding.corporate.companyDetail.latestNetProfit)}`}>
									<CurrencyInput
										name="latestNetProfit"
										currency={currency}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>
								</Form.Item>
							</Col>
						</Row>
					</div>
				</Form>
				<TitleBox>
					<span>{t(i18nKey.onboarding.corporate.contactDetail.title)}</span>
				</TitleBox>

				<ContactPerson accountForm={form} />

				<Row
					gutter={[
						{ xs: 0, sm: 0, md: 0, lg: 132 },
						{ xs: 20, sm: 20, md: 20, lg: 16 }
					]}>
					<Col span={24}>
						<Button block type="primary" onClick={onSaveClick} loading={saving}>
							{t(i18nKey.onboarding.saveAndNext)}
						</Button>
					</Col>
					<Col span={24}>
						<Button block type="primary" ghost onClick={onCancelClick}>
							{t(i18nKey.common.cancel)}
						</Button>
					</Col>
				</Row>
			</RegistrationWidget>
			<QuitConfirmPopup open={quitModalOpen} onClose={onModalClose} onQuit={onModalQuit} />
		</OnBoardingRegisterPage>
	);
};

export default CompanyDetailPage;
